import { relations, sql } from 'drizzle-orm'
import { index, integer, pgTable, text, timestamp, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'
import z from 'zod/v4'

import { user } from '../auth'
import { ProgramStatusEnum, SeatSourceTypeEnum } from './enums'
import { SeatEntitlement } from './seat-entitlement'

/**
 * A scholarship / sponsorship program that groups many single-school seat entitlements
 * (the "sponsorships"). Holds the duration + start-date template that is copied onto each
 * child entitlement at creation. `sourceType` is nullable so a program may mix source types.
 */
export const Program = pgTable(
  'program',
  {
    id: uuid().primaryKey().defaultRandom(),
    name: text().notNull(),
    sourceType: SeatSourceTypeEnum(),
    durationMonths: integer().notNull(),
    startDate: timestamp({ mode: 'date', withTimezone: true }).notNull(),
    endDate: timestamp({ mode: 'date', withTimezone: true }).notNull(),
    status: ProgramStatusEnum().notNull().default('draft'),
    createdBy: text().references(() => user.id, { onDelete: 'set null' }),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),
  },
  (t) => [index('idx_program_status').on(t.status)],
)

export const programRelations = relations(Program, ({ one, many }) => ({
  createdByUser: one(user, {
    fields: [Program.createdBy],
    references: [user.id],
  }),

  entitlements: many(SeatEntitlement),
}))

export const InsertProgramSchema = createInsertSchema(Program, {
  name: z.string().min(1),
  sourceType: z.enum(SeatSourceTypeEnum.enumValues).optional(),
  durationMonths: z.number().int().positive(),
  startDate: z.date(),
  endDate: z.date(),
  createdBy: z.string().optional(),
})
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .superRefine((data, ctx) => {
    if (data.endDate <= data.startDate) {
      ctx.addIssue({ code: 'custom', message: 'End date must be after start date', path: ['endDate'] })
    }
  })

export const UpdateProgramSchema = createUpdateSchema(Program, {
  name: z.string().min(1),
  status: z.enum(ProgramStatusEnum.enumValues),
})
  .omit({
    id: true,
    durationMonths: true,
    startDate: true,
    endDate: true,
    createdBy: true,
    createdAt: true,
    updatedAt: true,
  })
  .partial()

export const SelectProgramSchema = createSelectSchema(Program, {
  id: z.uuid(),
  name: z.string(),
  sourceType: z.enum(SeatSourceTypeEnum.enumValues).nullable(),
  durationMonths: z.number().int(),
  startDate: z.date(),
  endDate: z.date(),
  status: z.enum(ProgramStatusEnum.enumValues),
  createdBy: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date().nullable(),
})

export type InsertProgram = typeof Program.$inferInsert
export type UpdateProgram = z.infer<typeof UpdateProgramSchema>
export type SelectProgram = typeof Program.$inferSelect
